/**
 * Brief Welcome Tour — the ~2-minute first-login tour, per customer type.
 * Copy rules: ≤40 words, second person, no unexplained jargon, comparative framing.
 * narrationText is the spoken (TTS) script — slightly warmer than the card text.
 */
import type { TutorialStep } from "./types";

export const briefSteps: TutorialStep[] = [
  // ─── Pricing customers (U / Admin) ───
  {
    id: "brief-welcome-dashboard",
    area: "dashboard",
    tier: "brief",
    roles: ["U", "Admin"],
    title: "Welcome to Autumn!",
    description:
      "This is where you start each day. Top left: your monthly numbers. Top middle: your performance at a glance. Bottom: how bookings are coming in. Almost everything you need lives on this one screen.",
    narrationText:
      "Welcome to Autumn! I'm going to give you a quick tour — it takes about two minutes. This dashboard is where you'll start each day: your monthly numbers on the top left, your performance at a glance in the middle, and your incoming bookings below.",
    targetSelector: '[data-tutorial="dashboard-header"]',
    cardPosition: "bottom",
    route: "/dashboard",
    scrollIntoView: true,
  },
  {
    id: "brief-price-vs-competitors",
    area: "dashboard",
    tier: "brief",
    roles: ["U", "Admin"],
    title: "See how your price compares",
    description:
      "The black line is your room price. The others are your competitors. At a glance, you can see how your price compares to the market on any date.",
    narrationText:
      "This chart shows your room price as the black line, with your competitors around it. So on any date, you can instantly see how your price compares to the market.",
    targetSelector: '[data-tutorial="pricing-chart"]',
    cardPosition: "top",
    route: "/dashboard",
    scrollIntoView: true,
  },
  {
    id: "brief-pricing-agent-intro",
    area: "ai-copilot",
    tier: "brief",
    roles: ["U", "Admin"],
    title: "Meet your pricing agent",
    description:
      "This is your pricing agent. Give it your rules in plain English — like \"keep weekends above $300\" or \"stay 5% below the hotel next door\" — and it prices every night around them, automatically.",
    narrationText:
      "Now the most important part: your pricing agent. Give it your rules in plain English — like keep weekends above three hundred dollars, or stay five percent below the hotel next door — and it prices every night around your rules, automatically.",
    targetSelector: '[data-tutorial="ai-copilot-chat"]',
    cardPosition: "left",
    route: "/dashboard",
    chatbotState: "open",
    chatMode: "agent",
    scrollIntoView: false,
    waitForElement: true,
  },
  {
    id: "brief-pricing-agent-rules",
    area: "ai-copilot",
    tier: "brief",
    roles: ["U", "Admin"],
    title: "Set it and forget it",
    description:
      "Ask for changes anytime — \"bump up weekend rates\" — it shows you each change for approval. We encourage a little setup in your first couple weeks to customize it to your hotel, then it's set it and forget it.",
    narrationText:
      "You can also ask it for changes anytime — like bump up my weekend rates — and it always shows you the change for your approval first. We encourage you to do a little setup upfront in the first couple of weeks, to customize things to your hotel — and then it's set it and forget it.",
    targetSelector: '[data-tutorial="ai-copilot-chat"]',
    cardPosition: "left",
    route: "/dashboard",
    chatbotState: "open",
    chatMode: "agent",
    scrollIntoView: false,
    waitForElement: true,
  },
  {
    id: "brief-settings-competitors",
    area: "settings",
    tier: "brief",
    roles: ["U", "Admin"],
    title: "Share your market knowledge",
    description:
      "You know your local market better than any system. Tell us where you expect your price to sit against each competitor — we build your knowledge into every pricing decision.",
    narrationText:
      "Here in Settings, you can teach the agent your market. You know your local market better than anyone — so tell us where you expect your price to sit against each competitor. We take your views into consideration, and your knowledge flows into every pricing decision.",
    targetSelector: '[data-tutorial="pricing-comparison"]',
    cardPosition: "right",
    route: "/settings?tab=competitor&subtab=pricing",
    scrollIntoView: true,
    waitForElement: true,
  },
  {
    id: "brief-settings-seasons",
    area: "settings",
    tier: "brief",
    roles: ["U", "Admin"],
    title: "Sleep well at night",
    description:
      "Seasons were made to help you sleep at night. Set a floor and ceiling price for each season, and you have our guarantee: the AI will never price outside your limits.",
    narrationText:
      "And seasons were created to help you sleep at night. Set the lowest and highest price you're comfortable with for each season — and you have our guarantee that the AI will never go outside those rules.",
    targetSelector: '[data-tutorial="season-settings"]',
    cardPosition: "right",
    route: "/settings?tab=seasons",
    scrollIntoView: true,
    waitForElement: true,
  },
  {
    id: "brief-settings-digest",
    area: "settings",
    tier: "brief",
    roles: ["U", "Admin"],
    title: "Your numbers, in your inbox",
    description:
      "Don't feel like logging in every day? The Email Digest sends your key numbers straight to your inbox, on the schedule you choose — so Autumn keeps you posted either way.",
    narrationText:
      "And if you'd rather not log in every day — the Email Digest sends your key numbers straight to your inbox, on the schedule you choose. Autumn keeps you posted either way.",
    targetSelector: '[data-tutorial="email-digest-settings"]',
    cardPosition: "right",
    route: "/settings?tab=email-digest",
    scrollIntoView: true,
    waitForElement: true,
    skipIfTargetMissing: true,
  },
  {
    id: "brief-calendar-control",
    area: "calendar",
    tier: "brief",
    roles: ["U", "Admin"],
    title: "Want to change a price yourself?",
    description:
      "The Calendar is your manual control. Click any date to set your own price, or select many dates to change them in bulk. The agent always respects your changes.",
    narrationText:
      "If you ever want to change a price yourself, the Calendar is your manual control. Click any date to set your own price, or select a range to change many days at once. The agent always respects your changes.",
    targetSelector: '[data-tutorial="calendar-view"]',
    cardPosition: "top",
    route: "/calendar",
    scrollIntoView: true,
    waitForElement: true,
  },
  {
    id: "brief-chatbot-hotline",
    area: "ai-copilot",
    tier: "brief",
    roles: ["U", "Admin"],
    title: "Ask us anything, anytime",
    description:
      "This chat is your direct line. Ask about your numbers, prices, or anything else. Our team sees your questions and follows up personally — so use it freely!",
    narrationText:
      "This chat is your direct line to us. Ask it anything — your numbers, your prices, anything at all. Our team sees your questions and follows up personally, so please use it freely.",
    targetSelector: '[data-tutorial="ai-copilot-chat"]',
    cardPosition: "left",
    route: "/dashboard",
    chatbotState: "open",
    chatMode: "chat",
    scrollIntoView: false,
    waitForElement: true,
  },
  {
    id: "brief-help-icon",
    area: "ai-copilot",
    tier: "brief",
    roles: ["U", "Admin"],
    title: "Stuck? Help is one tap away",
    description:
      "See this question mark? Tap it whenever you need us — it's much faster than email. And for a deeper walkthrough of any area, open Take a Tour in the sidebar anytime.",
    narrationText:
      "One last thing — see this question mark? Tap it whenever you're stuck. It's much faster than emailing us. And if you'd like a deeper tour of any area, just open Take a Tour in the sidebar. Enjoy Autumn!",
    targetSelector: '[data-tutorial="chat-help"]',
    cardPosition: "left",
    route: "/dashboard",
    chatbotState: "open",
    scrollIntoView: false,
    waitForElement: true,
  },

  // ─── Marketing-only customers ───
  {
    id: "brief-mkt-welcome",
    area: "marketing",
    tier: "brief",
    roles: ["MarketingOnly"],
    title: "Welcome to Autumn!",
    description:
      "This is your home: how your ads turn into bookings. Everything on this page answers one question — is your marketing bringing you guests?",
    narrationText:
      "Welcome to Autumn! Let me show you around — it takes about two minutes. This is your marketing home: everything on this page answers one question — is your marketing bringing you guests?",
    targetSelector: '[data-tutorial="marketing-header"]',
    cardPosition: "bottom",
    route: "/marketing",
    scrollIntoView: true,
    waitForElement: true,
  },
  {
    id: "brief-mkt-trends",
    area: "marketing",
    tier: "brief",
    roles: ["MarketingOnly"],
    title: "Watch your numbers move",
    description:
      "This chart shows how your marketing changes over time — how many people saw your ads, clicked them, and booked. Use the dropdown to switch between numbers.",
    narrationText:
      "This chart shows how your marketing moves over time — how many people saw your ads, clicked them, and booked. Use the dropdown to switch between numbers.",
    targetSelector: "#trends-over-time",
    cardPosition: "right",
    route: "/marketing?tab=dashboard",
    scrollIntoView: true,
  },
  {
    id: "brief-mkt-funnel",
    area: "marketing",
    tier: "brief",
    roles: ["MarketingOnly"],
    title: "From first look to booked room",
    description:
      "The funnel shows each stage of a guest's journey: they see your ad, visit your site, start booking, and book. You can see exactly where people drop off.",
    narrationText:
      "The funnel shows each stage of a guest's journey — they see your ad, visit your site, start booking, and book. You can see exactly where people drop off along the way.",
    targetSelector: "#funnel-summary, #funnel-loading",
    cardPosition: "top",
    route: "/marketing?tab=dashboard",
    scrollIntoView: true,
  },
  {
    id: "brief-mkt-campaign-categories",
    area: "marketing",
    tier: "brief",
    roles: ["MarketingOnly"],
    title: "What each campaign does for you",
    description:
      "Brand Protection keeps you at the top when travelers search your hotel's name — so booking sites and competitors can't capture them. Discovery puts you in front of travelers who haven't heard of you yet.",
    narrationText:
      "This table shows what each campaign type does for you. Brand Protection keeps you at the top when travelers search your hotel's name, so booking sites can't capture them. And Discovery puts you in front of travelers who haven't heard of you yet.",
    targetSelector: "#campaign-summary",
    cardPosition: "top",
    route: "/marketing?tab=dashboard",
    scrollIntoView: true,
  },
  {
    id: "brief-mkt-insights",
    area: "marketing",
    tier: "brief",
    roles: ["MarketingOnly"],
    title: "What we noticed for you",
    description:
      "Insights are short notes from Autumn about what's working and what changed — like a quick briefing from your marketing team, updated automatically.",
    narrationText:
      "Insights are short notes from Autumn about what's working and what changed — like a quick briefing from your marketing team, updated automatically.",
    targetSelector: '[data-tutorial="insights-panel"]',
    cardPosition: "left",
    route: "/marketing?tab=dashboard",
    scrollIntoView: true,
  },
  {
    id: "brief-mkt-chatbot",
    area: "marketing",
    tier: "brief",
    roles: ["MarketingOnly"],
    title: "Ask us anything, anytime",
    description:
      "This chat is your direct line. Ask about your marketing, your traffic, or anything else. Our team sees your questions and follows up personally — so use it freely!",
    narrationText:
      "This chat is your direct line to us. Ask about your marketing, your traffic, anything at all. Our team sees your questions and follows up personally, so please use it freely.",
    targetSelector: '[data-tutorial="ai-copilot-chat"]',
    cardPosition: "left",
    route: "/marketing",
    chatbotState: "open",
    scrollIntoView: false,
    waitForElement: true,
  },
  {
    id: "brief-mkt-help",
    area: "marketing",
    tier: "brief",
    roles: ["MarketingOnly"],
    title: "Stuck? Help is one tap away",
    description:
      "See this question mark? Tap it whenever you need us — it's much faster than email. And for a deeper walkthrough of any area, open Take a Tour in the sidebar anytime.",
    narrationText:
      "One last thing — see this question mark? Tap it whenever you're stuck; it's much faster than emailing us. And for a deeper tour, open Take a Tour in the sidebar anytime. Enjoy Autumn!",
    targetSelector: '[data-tutorial="chat-help"]',
    cardPosition: "left",
    route: "/marketing",
    chatbotState: "open",
    scrollIntoView: false,
    waitForElement: true,
  },
];
